import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWeb3 } from '../context/Web3Context';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AdminAPIKeyManager from '../components/AdminAPIKeyManager';
import AdminSocialLinks from '../components/AdminSocialLinks';
import AdminProductManager from '../components/AdminProductManager';
import AdminUserNFTManager from '../components/AdminUserNFTManager';
import AdminSMMManager from '../components/AdminSMMManager';
import AdminServicesOverview from '../components/AdminServicesOverview';
import AdminDiscountManager from '../components/AdminDiscountManager';
import AdminGamesManager from '../components/AdminGamesManager';
import AdminPlayVaultConfig from '../components/AdminPlayVaultConfig';
import AdminChat from '../components/AdminChat';
import AdminSpotsManager from '../components/AdminSpotsManager';
import SpotifyDataViewer from '../components/SpotifyDataViewer';
import AdminArtistIdManager from '../components/AdminArtistIdManager';
import PhoneAIChat from '../components/PhoneAIChat';
import AdminTicketManager from '../components/AdminTicketManager';
import AdminAnalyticsOverview from '../components/AdminAnalyticsOverview';
import AdminShell from '../components/AdminShell';
import Card from '../components/primitives/Card';
import UserDetailModal from '../components/UserDetailModal';
import OrderDetailModal from '../components/OrderDetailModal';
import NFTDetailModal from '../components/NFTDetailModal';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { NFT_CONTRACT_ADDRESS } from '../config/contracts';
import { USDT_ESCROW_ABI, USDT_ESCROW_ADDRESS } from '../config/usdtEscrow';
import '../styles/theme.css';
import './Dashboard.css';

// Sidebar navigation config. Tab keys MUST match the activeTab values used
// in the body's conditional rendering (overview / nfts / users / …).
const NAV_GROUPS = [
  { label: 'Insights', items: [
    { key: 'overview', label: 'Overview', icon: '◴' },
  ]},
  { label: 'Commerce', items: [
    { key: 'smm',       label: 'SMM',           icon: '⬢' },
    { key: 'services-overview', label: 'Services Overview', icon: '◷' },
    { key: 'products',  label: 'Products',      icon: '▤' },
    { key: 'discounts', label: 'Discounts',     icon: '%' },
    { key: 'tickets',   label: 'PHONE Tickets', icon: '◉' },
  ]},
  { label: 'Users', items: [
    { key: 'users',         label: 'Users',                icon: '◍' },
    { key: 'user-nft-mgmt', label: 'Manage Users & NFTs',  icon: '◎' },
    { key: 'artist-ids',    label: 'Artist IDs',           icon: '♪' },
  ]},
  { label: 'Content', items: [
    { key: 'streaming',    label: 'Streaming Data', icon: '⏵' },
    { key: 'spots',        label: 'Spots',          icon: '✦' },
    { key: 'social-links', label: 'Social Links',   icon: '↗' },
  ]},
  { label: 'System', items: [
    { key: 'nfts',        label: 'NFTs',        icon: '⌬' },
    { key: 'games',       label: 'Games',       icon: '◆' },
    { key: 'game-config', label: 'Game Config', icon: '⚙' },
    { key: 'api-keys',    label: 'API Keys',    icon: '⚷' },
  ]},
  { label: 'Support', items: [
    { key: 'support', label: 'Support Inbox', icon: '✉' },
  ]},
];

const TITLE_MAP = {
  overview: 'Overview',
  smm: 'SMM',
  'services-overview': 'Services Overview',
  products: 'Products',
  discounts: 'Discounts',
  tickets: 'PHONE Tickets',
  users: 'Users',
  'user-nft-mgmt': 'Manage Users & NFTs',
  'artist-ids': 'Artist IDs',
  streaming: 'Streaming Data',
  spots: 'Spots',
  'social-links': 'Social Links',
  nfts: 'NFTs',
  games: 'Games',
  'game-config': 'Game Config',
  'api-keys': 'API Keys',
  support: 'Support Inbox',
};

function SpotifyConnectButton() {
  const [connected, setConnected] = useState(null);
  const check = useCallback(async () => {
    try {
      const res = await (await import('../utils/api')).default.get('/spotify/auth/status');
      setConnected(res.data.connected);
    } catch { setConnected(false); }
  }, []);
  useEffect(() => { check(); }, [check]);

  // Check URL params for redirect result
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('spotify') === 'connected') { check(); window.history.replaceState({}, '', window.location.pathname); }
    if (p.get('spotify') === 'error') { alert('Spotify connection failed. Check your SPOTIFY_REDIRECT_URI in .env.'); window.history.replaceState({}, '', window.location.pathname); }
  }, [check]);

  const connectUrl = (() => {
    const h = window.location.hostname;
    const base = (h === 'localhost' || h === '127.0.0.1')
      ? 'http://localhost:5000/api'
      : `${window.location.origin}/api`;
    return `${base}/spotify/auth/connect`;
  })();

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, padding: '12px 16px', background: '#0f172a', borderRadius: 10, border: '1px solid #1e293b' }}>
      <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Spotify OAuth (for Top Tracks):</span>
      {connected === null && <span style={{ color: '#6b7280', fontSize: '0.82rem' }}>Checking…</span>}
      {connected === true && <span style={{ color: '#34d399', fontSize: '0.82rem', fontWeight: 600 }}>✓ Connected</span>}
      {connected === false && <span style={{ color: '#f87171', fontSize: '0.82rem' }}>Not connected</span>}
      <a
        href={connectUrl}
        style={{ marginLeft: 'auto', background: '#1db954', color: '#fff', padding: '6px 14px', borderRadius: 6, fontSize: '0.82rem', fontWeight: 600, textDecoration: 'none' }}
      >
        {connected ? 'Reconnect Spotify' : 'Connect Spotify'}
      </a>
    </div>
  );
}

const AdminDashboard = () => {
  const { isAuthenticated } = useAuth();
  const { isConnected, account, web3 } = useWeb3();
  const [dashboardData, setDashboardData] = useState(null);
  const [nfts, setNfts] = useState([]);
  const [selectedNft, setSelectedNft] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersRefreshing, setUsersRefreshing] = useState(false);
  const [chainUsers, setChainUsers] = useState([]); // On-chain NFT owners
  const [creditsByWallet, setCreditsByWallet] = useState({});
  const [loading, setLoading] = useState(true);
  // Initial tab: honor ?tab=X if present (so links like /admin?tab=smm land
  // on the right panel), otherwise default to overview.
  const initialTab = (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const t = p.get('tab');
      return t || 'overview';
    } catch (_) { return 'overview'; }
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  // Deep-link straight to an order detail via ?order=<db_id> (e.g. from an
  // order notification), so clicking a notification opens the order itself.
  const initialOrder = (() => {
    try { return new URLSearchParams(window.location.search).get('order') || null; }
    catch (_) { return null; }
  })();
  const [selectedOrder, setSelectedOrder] = useState(initialOrder);
  const [selectedUser, setSelectedUser] = useState(null);

  // Other components (e.g. AdminAnalyticsOverview alerts) navigate to
  // /admin?tab=X and dispatch this event so we swap to the right tab
  // without a full reload — avoids the AdminRoute flash-403.
  useEffect(() => {
    const onSetTab = (e) => {
      const t = e?.detail?.tab;
      if (t) setActiveTab(t);
    };
    // Open a specific order's detail (from a notification deep-link / soft nav).
    const onOpenOrder = (e) => {
      const id = e?.detail?.orderId;
      if (id) { setActiveTab('smm'); setSelectedOrder(id); }
    };
    window.addEventListener('admin:setTab', onSetTab);
    window.addEventListener('admin:openOrder', onOpenOrder);
    return () => {
      window.removeEventListener('admin:setTab', onSetTab);
      window.removeEventListener('admin:openOrder', onOpenOrder);
    };
  }, []);
  const [nftsPage, setNftsPage] = useState(1);
  const [nftsLimit] = useState(12);
  const [nftsTotalPages, setNftsTotalPages] = useState(1);
  const [usersPage, setUsersPage] = useState(1);
  const [usersPerPage] = useState(20);
  const [chainData, setChainData] = useState({
    totalSupply: null,
    presaleMinted: null,
    publicPrice: null,
    presalePrice: null
  });

  const getPageItems = (current, total) => {
    if (total <= 1) return [1];
    const items = [];
    const windowSize = 2;
    const start = Math.max(2, current - windowSize);
    const end = Math.min(total - 1, current + windowSize);

    items.push(1);
    if (start > 2) items.push('ellipsis-left');
    for (let i = start; i <= end; i += 1) {
      items.push(i);
    }
    if (end < total - 1) items.push('ellipsis-right');
    items.push(total);
    return items;
  };

  const requestAdminWithFallback = async (path, opts = {}) => {
    const walletAddress = account || localStorage.getItem('walletAddress');
    const primary = `/admin${path}`;
    try {
      return await api.get(primary, opts);
    } catch (e) {
      const status = e?.response?.status;
      if ((status === 401 || status === 403 || status === 404) && walletAddress) {
        return await api.get(`/test${path}`, {
          ...(opts || {}),
          params: {
            ...(opts?.params || {}),
            walletAddress
          }
        });
      }
      throw e;
    }
  };

  const mergedUsersRows = useMemo(() => {
    const byWallet = new Map();

    chainUsers.forEach((u) => {
      const wallet = String(u.walletAddress || '').toLowerCase();
      if (!wallet) return;
      byWallet.set(wallet, {
        walletAddress: wallet,
        totalNFTs: Number(u.totalNFTs || 0),
        nftsOwned: Array.isArray(u.nftsOwned) ? u.nftsOwned : [],
        chainOwned: Number(u.totalNFTs || 0) > 0
      });
    });

    users.forEach((u) => {
      const wallet = String(u.walletAddress || '').toLowerCase();
      if (!wallet) return;
      const dbNfts = Array.isArray(u.nftsOwned)
        ? u.nftsOwned.map((x) => (typeof x === 'number' ? x : x?.tokenId)).filter((n) => Number.isFinite(n))
        : [];
      const existing = byWallet.get(wallet);
      if (existing) {
        if (!existing.totalNFTs && dbNfts.length) {
          existing.totalNFTs = dbNfts.length;
          existing.nftsOwned = dbNfts;
        }
      } else {
        byWallet.set(wallet, {
          walletAddress: wallet,
          totalNFTs: dbNfts.length,
          nftsOwned: dbNfts,
          chainOwned: false
        });
      }
    });

    return Array.from(byWallet.values()).sort((a, b) => {
      if (b.totalNFTs !== a.totalNFTs) return b.totalNFTs - a.totalNFTs;
      return a.walletAddress.localeCompare(b.walletAddress);
    });
  }, [chainUsers, users]);

  const usersTotalPages = Math.max(1, Math.ceil(mergedUsersRows.length / usersPerPage));

  const formatUsdt = (weiStr, decimals = 18, places = 2) => {
    try {
      const s = String(weiStr || '0');
      const big = BigInt(s);
      const base = 10n ** BigInt(decimals);
      const whole = big / base;
      const frac = (big % base).toString().padStart(decimals, '0').slice(0, Math.min(decimals, places));
      return `${whole.toString()}.${frac.padEnd(places, '0')}`;
    } catch (_) {
      return '0.00';
    }
  };

  // Fetch chain data from contract
  const fetchChainData = async () => {
    if (!NFT_CONTRACT_ADDRESS) return;
    
    // Always use read-only BSC RPC for admin chain reads (stable even if wallet provider is flaky)
    const { Web3 } = await import('web3');
    const BSC_RPC_URLS = [
      'https://bsc-dataseed.bnbchain.org',
      'https://bsc-dataseed1.bnbchain.org',
      'https://bsc-dataseed2.bnbchain.org',
      'https://bsc-rpc.publicnode.com',
      'https://1rpc.io/bnb'
    ];
    // Pick the first RPC that actually responds — public endpoints flap, and a
    // dead one would silently drop ownerOf() reads (showing fewer NFTs).
    let web3Instance = null;
    for (const url of BSC_RPC_URLS) {
      try {
        const w = new Web3(url);
        await w.eth.getBlockNumber();
        web3Instance = w;
        break;
      } catch (_) { /* try next endpoint */ }
    }
    if (!web3Instance) return;
    
    try {
      // Avoid creating web3 Contract instances (prevents subscription-manager listener leaks in dev)
      const call = async (data) => web3Instance.eth.call({ to: NFT_CONTRACT_ADDRESS, data });
      const decodeUint = (hex) => web3Instance.eth.abi.decodeParameter('uint256', hex);
      const decodeAddr = (hex) => web3Instance.eth.abi.decodeParameter('address', hex);

      const fn = {
        totalSupply: web3Instance.eth.abi.encodeFunctionSignature('totalSupply()'),
        presaleMinted: web3Instance.eth.abi.encodeFunctionSignature('presaleMinted()'),
        presalePrice: web3Instance.eth.abi.encodeFunctionSignature('PRESALE_PRICE()'),
        publicPrice: web3Instance.eth.abi.encodeFunctionSignature('PUBLIC_PRICE()'),
        ownerOf: web3Instance.eth.abi.encodeFunctionSignature('ownerOf(uint256)')
      };

      // Encoders for parameterized reads.
      const encodeOwnerOf = (tokenId) => web3Instance.eth.abi.encodeFunctionCall(
        { name: 'ownerOf', type: 'function', inputs: [{ name: 'tokenId', type: 'uint256' }] },
        [String(tokenId)]
      );
      const encodeTokenByIndex = (idx) => web3Instance.eth.abi.encodeFunctionCall(
        { name: 'tokenByIndex', type: 'function', inputs: [{ name: 'index', type: 'uint256' }] },
        [String(idx)]
      );

      const [totalSupplyHex, presaleMintedHex, presalePriceHex, publicPriceHex] = await Promise.all([
        call(fn.totalSupply),
        call(fn.presaleMinted),
        call(fn.presalePrice),
        call(fn.publicPrice)
      ]);

      const totalSupply = decodeUint(totalSupplyHex);
      const presaleMinted = decodeUint(presaleMintedHex);
      const presalePrice = decodeUint(presalePriceHex);
      const publicPrice = decodeUint(publicPriceHex);

      const totalSupplyNum = parseInt(totalSupply.toString(), 10);
      
      setChainData({
        totalSupply: totalSupplyNum,
        presaleMinted: parseInt(presaleMinted.toString(), 10),
        publicPrice: parseFloat(web3Instance.utils.fromWei(publicPrice.toString(), 'ether')),
        presalePrice: parseFloat(web3Instance.utils.fromWei(presalePrice.toString(), 'ether'))
      });

      // Fetch all NFT owners from chain.
      //
      // Strategy A (preferred): ERC721Enumerable's tokenByIndex(i) — returns
      //   the actual token ID at index i, so non-sequential mints (presale at
      //   one range, public at another, burns leaving gaps) are caught.
      // Strategy B (fallback): sequential ownerOf(start..start+totalSupply-1)
      //   — same scheme the table used historically. Works for contracts that
      //   mint sequentially (the common case) even without enumerable.
      //
      // Calls are batched (8 at a time) to stay well under the public BSC
      // RPC's rate limit. A wider blind probe would hit 429s and return zero.
      if (totalSupplyNum > 0) {
        const runBatched = async (items, fn, batchSize = 8) => {
          const out = [];
          for (let i = 0; i < items.length; i += batchSize) {
            const slice = items.slice(i, i + batchSize);
            // eslint-disable-next-line no-await-in-loop
            const part = await Promise.all(slice.map(fn));
            out.push(...part);
          }
          return out;
        };

        // ── Strategy A: tokenByIndex ───────────────────────────────────────
        let tokenIds = [];
        try {
          const indices = Array.from({ length: totalSupplyNum }, (_, i) => i);
          const fromIndex = await runBatched(indices, (i) =>
            call(encodeTokenByIndex(i))
              .then((hex) => Number(decodeUint(hex).toString()))
              .catch(() => null)
          );
          const good = fromIndex.filter((n) => Number.isFinite(n));
          if (good.length === totalSupplyNum) tokenIds = good;
        } catch (_) { /* fall through */ }

        // ── Strategy B: sequential ownerOf ─────────────────────────────────
        // Only runs if tokenByIndex didn't return a complete set. This is the
        // same scheme the table used before — proven to work with this
        // contract — so we KEEP it as the safety net.
        if (tokenIds.length === 0) {
          let startAtZero = false;
          try {
            const owner0 = decodeAddr(await call(encodeOwnerOf(0)));
            if (owner0 && owner0 !== '0x0000000000000000000000000000000000000000') startAtZero = true;
          } catch (_) { startAtZero = false; }

          const start = startAtZero ? 0 : 1;
          tokenIds = Array.from({ length: totalSupplyNum }, (_, i) => start + i);
        }

        // Resolve owners (batched).
        const owners = await runBatched(tokenIds, (tokenId) =>
          call(encodeOwnerOf(tokenId))
            .then((hex) => {
              const owner = decodeAddr(hex);
              return owner && owner !== '0x0000000000000000000000000000000000000000'
                ? { tokenId, owner: owner.toLowerCase() }
                : null;
            })
            .catch(() => null)
        );

        const ownerMap = new Map();
        owners.filter(Boolean).forEach(({ tokenId, owner }) => {
          if (!ownerMap.has(owner)) ownerMap.set(owner, { walletAddress: owner, nftsOwned: [], totalNFTs: 0 });
          ownerMap.get(owner).nftsOwned.push(tokenId);
          ownerMap.get(owner).totalNFTs += 1;
        });

        const chainUsersList = Array.from(ownerMap.values()).sort((a, b) => b.totalNFTs - a.totalNFTs);
        setChainUsers(chainUsersList);
      } else {
        setChainUsers([]);
      }
    } catch (error) {
      console.error('Error fetching chain data:', error);
    }
  };

  useEffect(() => {
    fetchAdminData();
    fetchNftsPage(1);
    fetchChainData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch chain data when web3 connection changes (or periodically if not connected)
  useEffect(() => {
    fetchChainData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [web3, isConnected]);

  useEffect(() => {
    if (nftsPage) {
      fetchNftsPage(nftsPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nftsPage, isAuthenticated, account]);

  useEffect(() => {
    if (usersPage > usersTotalPages) {
      setUsersPage(1);
    }
  }, [usersPage, usersTotalPages]);

  useEffect(() => {
    const fetchUsersCredits = async () => {
      try {
        if (!mergedUsersRows.length) {
          setCreditsByWallet({});
          return;
        }
        const pageRows = mergedUsersRows.slice(
          (usersPage - 1) * usersPerPage,
          (usersPage - 1) * usersPerPage + usersPerPage
        );
        const wallets = pageRows
          .map((u) => String(u.walletAddress || '').toLowerCase())
          .filter(Boolean);

        const nextCredits = {};

        // Primary source: read escrow balances directly from BSC.
        if (USDT_ESCROW_ADDRESS) {
          try {
            const { Web3 } = await import('web3');
            const roWeb3 = new Web3('https://bsc-dataseed.bnbchain.org');
            const escrow = new roWeb3.eth.Contract(USDT_ESCROW_ABI, USDT_ESCROW_ADDRESS);
            const entries = await Promise.all(
              wallets.map(async (wallet) => {
                try {
                  const [available, reserved] = await Promise.all([
                    escrow.methods.availableBalance(wallet).call(),
                    escrow.methods.reservedBalance(wallet).call()
                  ]);
                  return [wallet, { available: String(available), reserved: String(reserved) }];
                } catch (_) {
                  return [wallet, null];
                }
              })
            );
            entries.forEach(([wallet, credits]) => {
              if (credits) nextCredits[wallet] = credits;
            });
          } catch (e) {
            console.error('Direct BSC credits read failed:', e);
          }
        }

        // Fallback to backend endpoint for unresolved wallets
        const unresolved = wallets.filter((w) => !nextCredits[w]);
        if (unresolved.length > 0) {
          const res = await requestAdminWithFallback('/users/credits', {
            params: { wallets: unresolved.join(',') }
          });
          const fromApi = res?.data?.credits || {};
          unresolved.forEach((wallet) => {
            if (fromApi[wallet]) nextCredits[wallet] = fromApi[wallet];
          });
        }

        setCreditsByWallet(nextCredits);
      } catch (error) {
        console.error('Users credits error:', error);
      }
    };
    fetchUsersCredits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mergedUsersRows, usersPage, usersPerPage]);

  const fetchAdminData = async () => {
    try {
      setLoading(true);
      
      const [dashboardRes, usersRes] = await Promise.allSettled([
        requestAdminWithFallback('/dashboard').catch(e => ({ error: e })),
        requestAdminWithFallback('/users').catch(e => ({ error: e }))
      ]);

      // Handle dashboard response
      if (dashboardRes.status === 'fulfilled' && !dashboardRes.value.error) {
        setDashboardData(dashboardRes.value.data);
      } else {
        console.error('Dashboard error:', dashboardRes.value?.error || dashboardRes.reason);
        toast.error('Failed to load dashboard overview');
      }

      // Handle users response
      if (usersRes.status === 'fulfilled' && !usersRes.value.error) {
        setUsers(usersRes.value.data.users || []);
      } else {
        console.error('Users error:', usersRes.value?.error || usersRes.reason);
        toast.error('Failed to load users');
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Re-pull the users list (DB) + on-chain NFT snapshot. The per-wallet escrow
  // credits effect re-runs automatically once `users` updates.
  const refreshUsers = async () => {
    setUsersRefreshing(true);
    try {
      await Promise.all([fetchAdminData(), fetchChainData()]);
    } finally {
      setUsersRefreshing(false);
    }
  };

  const fetchNftsPage = async (page) => {
    try {
      const response = await requestAdminWithFallback('/nfts', {
        params: {
          page,
          limit: nftsLimit
        }
      });
      setNfts(response.data.nfts || []);
      setNftsTotalPages(response.data.pagination?.pages || 1);
      if (page > (response.data.pagination?.pages || 1)) {
        setNftsPage(1);
      }
    } catch (error) {
      console.error('NFTs error:', error);
      setNfts([]);
    }
  };

  const usersTableRows = mergedUsersRows;
  const pagedUsersRows = usersTableRows.slice(
    (usersPage - 1) * usersPerPage,
    (usersPage - 1) * usersPerPage + usersPerPage
  );

  if (loading) {
    return (
      <div className="dashboard-container">
        <Header />
        <div className="loading-container">
          <div>Loading admin dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Header />
      
      <div className="dashboard-content">
        <AdminShell
          groups={NAV_GROUPS}
          activeKey={activeTab}
          onChange={setActiveTab}
          title={TITLE_MAP[activeTab] || 'Admin'}
          /* No topbarRight — the shared <Header /> already mounts the
             <NotificationBell />, no need to duplicate it here. */
        >
        {/* Legacy tab buttons left mounted but hidden; the AdminShell sidebar
            drives navigation now. Kept temporarily so any stray refs to .tab
            CSS don't 404; will be deleted in the next cleanup pass. */}
        <div className="dashboard-tabs" style={{ display: 'none' }}>
          <button
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`tab ${activeTab === 'nfts' ? 'active' : ''}`}
            onClick={() => setActiveTab('nfts')}
          >
            NFTs ({dashboardData?.overview?.totalNFTs || 0})
          </button>
          <button
            className={`tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            Users ({dashboardData?.overview?.totalUsers || 0})
          </button>
          <button
            className={`tab ${activeTab === 'user-nft-mgmt' ? 'active' : ''}`}
            onClick={() => setActiveTab('user-nft-mgmt')}
          >
            Manage Users & NFTs
          </button>
          <button
            className={`tab ${activeTab === 'streaming' ? 'active' : ''}`}
            onClick={() => setActiveTab('streaming')}
          >
            Streaming Data
          </button>
          <button
            className={`tab ${activeTab === 'artist-ids' ? 'active' : ''}`}
            onClick={() => setActiveTab('artist-ids')}
          >
            Artist IDs
          </button>
          <button
            className={`tab ${activeTab === 'api-keys' ? 'active' : ''}`}
            onClick={() => setActiveTab('api-keys')}
          >
            API Keys
          </button>
          <button
            className={`tab ${activeTab === 'social-links' ? 'active' : ''}`}
            onClick={() => setActiveTab('social-links')}
          >
            Social Links
          </button>
          <button
            className={`tab ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            Products
          </button>
          <button
            className={`tab ${activeTab === 'smm' ? 'active' : ''}`}
            onClick={() => setActiveTab('smm')}
          >
            SMM
          </button>
          <button
            className={`tab ${activeTab === 'discounts' ? 'active' : ''}`}
            onClick={() => setActiveTab('discounts')}
          >
            Discounts
          </button>
          <button
            className={`tab ${activeTab === 'games' ? 'active' : ''}`}
            onClick={() => setActiveTab('games')}
          >
            Games
          </button>
          <button
            className={`tab ${activeTab === 'game-config' ? 'active' : ''}`}
            onClick={() => setActiveTab('game-config')}
          >
            Game Config
          </button>
          <button
            className={`tab ${activeTab === 'spots' ? 'active' : ''}`}
            onClick={() => setActiveTab('spots')}
          >
            Spots
          </button>
          <button
            className={`tab ${activeTab === 'support' ? 'active' : ''}`}
            onClick={() => setActiveTab('support')}
          >
            Support Inbox
          </button>
          <button
            className={`tab ${activeTab === 'tickets' ? 'active' : ''}`}
            onClick={() => setActiveTab('tickets')}
          >
            PHONE Tickets
          </button>
        </div>

        <div className="dashboard-body">
          {activeTab === 'overview' && (
            <AdminAnalyticsOverview />
          )}

          {activeTab === 'nfts' && (
            <Card noPad><div className="nfts-tab">
              <div className="nfts-grid">
                {nfts.length === 0 ? (
                  <p>No NFTs found.</p>
                ) : (
                  nfts.map((nft) => (
                    <div
                      key={nft._id}
                      className="nft-card nft-card--clickable"
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedNft(nft)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedNft(nft); } }}
                      title="Click for full detail"
                    >
                      <div className="nft-image">
                        {nft.metadata?.image ? (
                          <img src={nft.metadata.image} alt={nft.metadata.name} />
                        ) : (
                          <div className="nft-placeholder">NFT #{nft.tokenId}</div>
                        )}
                      </div>
                      <div className="nft-info">
                        <h4>{nft.metadata?.name || `NFT #${nft.tokenId}`}</h4>
                        <p>Owner: {nft.owner ? `${nft.owner.substring(0, 8)}...` : 'Unknown'}</p>
                        <p>Streams: {nft.streamingData?.totalStreams || 0}</p>
                        <p>Revenue: ${(nft.streamingData?.totalRevenue || 0).toFixed(2)}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {nftsTotalPages > 1 && (
                <div className="pagination">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setNftsPage(Math.max(1, nftsPage - 1))}
                    disabled={nftsPage === 1}
                  >
                    Previous
                  </button>
                  <div className="pagination-numbers">
                    {getPageItems(nftsPage, nftsTotalPages).map((item) => {
                      if (typeof item !== 'number') {
                        return (
                          <span key={item} className="pagination-ellipsis">…</span>
                        );
                      }
                      return (
                        <button
                          key={item}
                          className={`pagination-page ${item === nftsPage ? 'active' : ''}`}
                          onClick={() => setNftsPage(item)}
                          disabled={item === nftsPage}
                        >
                          {item}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setNftsPage(Math.min(nftsTotalPages, nftsPage + 1))}
                    disabled={nftsPage >= nftsTotalPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </div></Card>
          )}

          {activeTab === 'users' && (
            <Card noPad><div className="users-tab">
              {(() => {
                const totalNftsOnChain = chainUsers.reduce((s, u) => s + Number(u.totalNFTs || 0), 0);
                const walletsWithNfts = chainUsers.filter((u) => Number(u.totalNFTs || 0) > 0).length;
                const totalRows = mergedUsersRows.length;
                return (
                  <div style={{ marginBottom: '1rem', padding: '14px 16px', background: 'rgba(0, 224, 255, 0.08)', borderRadius: '10px', border: '1px solid rgba(0, 224, 255, 0.25)', display: 'flex', flexWrap: 'wrap', gap: '18px', alignItems: 'center' }}>
                    <span style={{ color: 'var(--gold-primary)', fontSize: '11px', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase' }}>Chain snapshot</span>
                    <span style={{ color: 'var(--text-primary)', fontSize: '0.92rem' }}>
                      <strong style={{ color: 'var(--gold-secondary, #b06bff)' }}>{totalNftsOnChain}</strong> NFT{totalNftsOnChain === 1 ? '' : 's'} across <strong style={{ color: 'var(--gold-secondary, #b06bff)' }}>{walletsWithNfts}</strong> wallet{walletsWithNfts === 1 ? '' : 's'}
                    </span>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.86rem' }}>
                      · {totalRows} total row{totalRows === 1 ? '' : 's'} (merged with DB users)
                    </span>
                    {walletsWithNfts === 1 && totalNftsOnChain > 1 && (
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', fontStyle: 'italic' }}>
                        Note: all {totalNftsOnChain} NFTs are owned by a single wallet — likely the minter. The table groups by wallet, so that's why you see one row with NFTs.
                      </span>
                    )}
                    <span style={{ marginLeft: 'auto', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                      Click a row for full user profile.
                    </span>
                    <button
                      type="button"
                      onClick={refreshUsers}
                      disabled={usersRefreshing}
                      title="Reload the users list and on-chain NFT snapshot"
                      style={{
                        padding: '6px 12px', borderRadius: '8px', whiteSpace: 'nowrap',
                        border: '1px solid rgba(0, 224, 255, 0.35)', background: 'rgba(0, 224, 255, 0.1)',
                        color: '#00e0ff', fontWeight: 700, fontSize: '0.8rem',
                        cursor: usersRefreshing ? 'default' : 'pointer', opacity: usersRefreshing ? 0.55 : 1,
                      }}
                    >
                      {usersRefreshing ? 'Refreshing…' : '↻ Refresh users'}
                    </button>
                  </div>
                );
              })()}
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Wallet Address</th>
                    <th>NFTs Owned (Chain)</th>
                    <th>Token IDs</th>
                    <th>Owns NFT (Chain)</th>
                    <th>SMM Credits (Avail / Res)</th>
                    <th>Total Revenue (DB)</th>
                    <th>Is Admin</th>
                  </tr>
                </thead>
                <tbody>
                  {usersTableRows.length > 0 ? (
                    pagedUsersRows.map((user, index) => {
                      // Find matching DB user if exists
                      const dbUser = users.find(u => u.walletAddress.toLowerCase() === user.walletAddress.toLowerCase());
                      return (
                        <tr
                          key={user.walletAddress || index}
                          onClick={() => setSelectedUser(user.walletAddress)}
                          style={{ cursor: 'pointer' }}
                          title="Click to view full user profile"
                        >
                          <td>
                            <code style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                              {user.walletAddress}
                            </code>
                          </td>
                          <td>
                            <strong style={{ color: 'var(--gold-primary)' }}>{user.totalNFTs}</strong>
                          </td>
                          <td>
                            <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                              {user.nftsOwned.slice(0, 5).map(id => `#${id}`).join(', ')}
                              {user.nftsOwned.length > 5 && ` +${user.nftsOwned.length - 5} more`}
                            </span>
                          </td>
                          <td>{user.chainOwned ? 'Yes' : 'No'}</td>
                          <td>
                            {(() => {
                              const c = creditsByWallet[String(user.walletAddress || '').toLowerCase()];
                              if (!c) return '— / —';
                              return `${formatUsdt(c.available)} / ${formatUsdt(c.reserved)}`;
                            })()}
                          </td>
                          <td>${(dbUser?.totalRevenue || 0).toFixed(2)}</td>
                          <td>{dbUser?.isAdmin ? 'Yes' : 'No'}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                        {chainData.totalSupply === null ? 'Loading on-chain data...' : 'No users found yet'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {usersTotalPages > 1 && (
                <div className="pagination">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setUsersPage(Math.max(1, usersPage - 1))}
                    disabled={usersPage === 1}
                  >
                    Previous
                  </button>
                  <div className="pagination-numbers">
                    {getPageItems(usersPage, usersTotalPages).map((item) => {
                      if (typeof item !== 'number') {
                        return (
                          <span key={item} className="pagination-ellipsis">…</span>
                        );
                      }
                      return (
                        <button
                          key={item}
                          className={`pagination-page ${item === usersPage ? 'active' : ''}`}
                          onClick={() => setUsersPage(item)}
                          disabled={item === usersPage}
                        >
                          {item}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setUsersPage(Math.min(usersTotalPages, usersPage + 1))}
                    disabled={usersPage >= usersTotalPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </div></Card>
          )}

          {/* Each sub-panel is wrapped in <Card noPad> for unified chrome
              (gold accent line, brand border + bg). The AdminShell topbar
              already shows the section name so we omit the Card title to
              avoid duplication. `noPad` lets each component keep its own
              internal padding. */}
          {activeTab === 'user-nft-mgmt' && (
            <Card noPad><AdminUserNFTManager /></Card>
          )}
          {activeTab === 'streaming' && (
            <Card noPad><SpotifyDataViewer /></Card>
          )}
          {activeTab === 'artist-ids' && (
            <Card noPad>
              <SpotifyConnectButton />
              <AdminArtistIdManager />
            </Card>
          )}
          {activeTab === 'api-keys' && (
            <Card noPad><AdminAPIKeyManager chainUsers={chainUsers} /></Card>
          )}
          {activeTab === 'social-links' && (
            <Card noPad><AdminSocialLinks /></Card>
          )}
          {activeTab === 'products' && (
            <Card noPad><AdminProductManager /></Card>
          )}
          {activeTab === 'smm' && (
            <Card noPad><AdminSMMManager /></Card>
          )}
          {activeTab === 'services-overview' && (
            <Card noPad><AdminServicesOverview /></Card>
          )}
          {activeTab === 'discounts' && (
            <Card noPad><AdminDiscountManager /></Card>
          )}
          {activeTab === 'games' && (
            <Card noPad><AdminGamesManager /></Card>
          )}
          {activeTab === 'game-config' && (
            <Card noPad><AdminPlayVaultConfig /></Card>
          )}
          {activeTab === 'spots' && (
            <Card noPad><AdminSpotsManager /></Card>
          )}
          {activeTab === 'support' && (
            <Card noPad><AdminChat /></Card>
          )}
          {activeTab === 'tickets' && (
            <Card noPad><AdminTicketManager /></Card>
          )}
        </div>
        </AdminShell>
      </div>
      <Footer />
      {/* PHONE — native AI agent. Phase 1: admin read-only. */}
      <PhoneAIChat persona="admin" />

      {/* Admin drill-down modals — clicking a user row or order row opens
          these. UserDetail can in turn open OrderDetail via callback. */}
      <UserDetailModal
        wallet={selectedUser}
        onClose={() => setSelectedUser(null)}
        onSelectOrder={(id) => setSelectedOrder(id)}
      />
      <OrderDetailModal
        orderId={selectedOrder}
        onClose={() => setSelectedOrder(null)}
      />
      <NFTDetailModal
        nft={selectedNft}
        onClose={() => setSelectedNft(null)}
      />
    </div>
  );
};

export default AdminDashboard;
